import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShortlistProfile } from '../types';
import { fetchShortlistById, fetchProfilesByIds, Profile } from '../services/shortlistService';
import { ProfileCard, ProfileDetailsDrawer } from '../../search';
import { useShortlistContext } from '../../../contexts/ShortlistContext';

const ShortlistSubmittedPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [shortlistedProfiles, setShortlistedProfiles] = useState<ShortlistProfile[]>([]);
  const [fullProfiles, setFullProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    addProfileToShortlist,
    removeFromShortlist,
    isInShortlist,
  } = useShortlistContext();

  // Convert Profile to ShortlistProfile format
  const convertProfileToShortlistProfile = (profile: Profile): ShortlistProfile => {
    const totalExperience = profile.experiences.reduce((total, exp) => total + exp.totalDurationMonths, 0);
    const yearsExperience = Math.round(totalExperience / 12);

    return {
      id: profile.id,
      title: profile.experiences[0]?.roles[0]?.title || 'Software Engineer',
      experience: `${yearsExperience} years of experience`,
      industries: profile.industries || [],
      skills: profile.experiences[0]?.roles[0]?.skills || [],
      summary: profile.summary
    };
  };

  useEffect(() => {
    const loadShortlistData = async () => {
      // If we have a shortlist ID in the URL, fetch from API
      if (id) {
        setLoading(true);
        setError(null);

        try {
          const shortlistData = await fetchShortlistById(id);
          const profiles = await fetchProfilesByIds(shortlistData.shortlistIds);
          const shortlistProfiles = profiles.map(convertProfileToShortlistProfile);
          setShortlistedProfiles(shortlistProfiles);
          setFullProfiles(profiles);
        } catch (err) {
          setError('Failed to load shortlist data');
          console.error('Error loading shortlist:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    loadShortlistData();
  }, [id]);

  const handleBackToTalentPool = () => {
    navigate('/');
  };

  const handleViewProfile = (profileData: any) => {
    // Find the full profile data by ID
    const fullProfile = fullProfiles.find(p => p.id === profileData.id);
    if (fullProfile) {
      setSelectedProfile(fullProfile);
      setIsDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedProfile(null);
  };

  const handleBookmark = (profileId: string) => {
    if (isInShortlist(profileId)) {
      removeFromShortlist(profileId);
    } else {
      // Find the profile data and add it to shortlist
      const profile = shortlistedProfiles.find(p => p.id === profileId);
      if (profile) {
        addProfileToShortlist({
          id: profile.id,
          title: profile.title,
          experience: profile.experience,
          skills: profile.skills,
          industries: profile.industries,
        });
      }
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dreamdev-bg-primary)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading shortlist...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dreamdev-bg-primary)' }}>
        <div className="text-center">
          <p style={{ color: '#EF4444', fontSize: '18px', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={handleBackToTalentPool}
            className="px-6 py-3 text-sm font-bold uppercase transition-colors duration-200"
            style={{
              backgroundColor: '#E9F7FF',
              color: '#03238C',
              fontSize: 'var(--dreamdev-font-size-sm)',
              fontWeight: '700',
              borderRadius: '6px',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back to Talent Pool
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{
      overflow: 'hidden',
      background: 'linear-gradient(185deg, var(--white, #ffffff) 0%, var(--white_text_on_dark, rgb(233, 247, 255)) 13%, var(--white, rgb(255, 255, 255)) 45%, var(--white, rgb(255, 255, 255)) 100%)'
    }}>
      {/* Background decorative image */}
      <img
        src="/logos/yellow-blue.svg"
        alt="Decorative background element"
        className="absolute pointer-events-none"
        style={{
          height: '1200px',
          right: '-400px',
          top: '223px',
          zIndex: 0,
          rotate: '406deg',
          opacity: 0.5
        }}
      />
      <div className="container mx-auto px-4 py-12 max-w-6xl relative z-10">
        {/* Header Section */}
        <div className="mb-12">
          {/* Orange accent line */}
          <div
            className="mb-2"
            style={{
              width: '30px',
              height: '2px',
              backgroundColor: '#FF5C3E',
              borderRadius: '2px'
            }}
          />

          {/* Main Title */}
          <h1
            className="mb-4"
            style={{
              fontSize: '38px',
              fontWeight: '700',
              color: '#03238C',
              fontFamily: 'Source Sans 3, sans-serif',
              lineHeight: '1.2'
            }}
          >
            Shortlist Submitted Successfully
          </h1>

          {/* Confirmation Message */}
          <p
            className="mb-4"
            style={{
              fontSize: '18px',
              color: '#6B7280',
              fontWeight: '400',
              fontFamily: 'Source Sans 3, sans-serif',
              lineHeight: '1.6'
            }}
          >
            Thank you for sharing your shortlist. Our team is reviewing your selections and will get in touch shortly to discuss availability and next steps.
          </p>

          {/* Timeline */}
          <p
            className="mb-8"
            style={{
              fontSize: '18px',
              color: '#03238C',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif',
              lineHeight: '1.4'
            }}
          >
            Expect to hear from us within 24 hours.
          </p>
        </div>

        {/* Your Shortlist Section */}
        <div className="mb-12">
          <h2
            className="mb-6"
            style={{
              fontSize: '18px',
              color: '#FF5C3E',
              fontWeight: '700',
              fontFamily: 'Inter, sans-serif'
            }}
          >
            Your Shortlist
          </h2>

          {/* Profile Cards */}
          {shortlistedProfiles.length > 0 ? (
            <div className="space-y-4">
              {shortlistedProfiles.map((profile) => (
                <ProfileCard
                  key={profile.id}
                  id={profile.id}
                  title={profile.title}
                  experience={profile.experience}
                  verification="Verified by DreamDev"
                  skills={profile.skills}
                  industry=""
                  industries={profile.industries}
                  summary={profile.summary}
                  onBookmark={() => handleBookmark(profile.id)}
                  isBookmarked={isInShortlist(profile.id)}
                  profileData={profile}
                  onViewProfile={handleViewProfile}
                  hideShortlistButton={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p
                style={{
                  fontSize: '16px',
                  color: '#6B7280',
                  fontWeight: '400',
                  fontFamily: 'Source Sans 3, sans-serif'
                }}
              >
                No shortlisted profiles found. Please go back to the talent pool to add profiles to your shortlist.
              </p>
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        <div className="text-center flex flex-col items-start">
          <p
            className="mb-6 text-center"
            style={{
              fontSize: '18px',
              color: '#03238C',
              fontWeight: '400',
              fontFamily: 'Source Sans 3, sans-serif',
              lineHeight: '1.6'
            }}
          >
            Want to keep browsing? You can continue exploring profiles while we process your request.
          </p>

          <button
            onClick={handleBackToTalentPool}
            className="px-6 py-3 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200"
            style={{
              backgroundColor: '#E9F7FF',
              color: '#03238C',
              fontSize: 'var(--dreamdev-font-size-sm)',
              fontWeight: '700',
              borderRadius: '6px',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.backgroundColor = '#D1EFFF';
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.backgroundColor = '#E9F7FF';
            }}
          >
            <span>BACK TO TALENT POOL</span>
          </button>
        </div>
      </div>

      {/* Profile Details Drawer */}
      <ProfileDetailsDrawer
        profile={selectedProfile}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onAddToShortlist={handleBookmark}
        isInShortlist={selectedProfile ? isInShortlist(selectedProfile.id) : false}
        hideShortlistButton={true}
      />
    </div>
  );
};

export default ShortlistSubmittedPage;
